import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  addEdge,
  type Connection,
  Controls,
  type Edge,
  type Node,
  type NodeChange,
  useEdgesState,
  useNodesState
} from 'reactflow';
import 'reactflow/dist/style.css';
import { createApiUrl } from '../../../config/api';
import { useApiAuth } from '../../../hooks/useApiAuth';
import type { components } from '../../../types/api-generated';
import type { ChatMessage } from '../../ChatBox/Messages';
import EquationModal from './EquationModal';
import { VariableEdge } from './ModelEdge';
import { ModelBlockNode } from './ModelNode';

// Simple global position cache - stores node positions by node name
const nodePositionCache = new Map<string, { x: number; y: number }>();

// Use backend-generated ModelGraph type
type ModelGraphData = components["schemas"]["ModelGraph-Output"];
type ModelNodeOutput = components["schemas"]["ModelNode-Output"];
type ModelBlock = ModelNodeOutput["model"];

interface PredictorModelData {
  model_name: string;
  model_object: any;
  model_graph: ModelGraphData;
}

interface PredictorModelProps {
  predictorModelData: PredictorModelData;
  tabId: string;
  userID?: string;
  sessionId?: string;
  onTrainingMessage?: (message: ChatMessage) => void;
  onResponse?: (data: any[]) => void;
  images?: Array<{ file_name: string; file_type: string }>; // Images from the tab
}

// Custom image node component for training results
const ImageNode = ({ data }: { data: any }) => {
  return (
    <div style={{
      background: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      padding: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      minWidth: '200px',
      maxWidth: '300px'
    }}>
      <div style={{
        fontSize: '12px',
        fontWeight: 'bold',
        marginBottom: '8px',
        textAlign: 'center',
        color: '#374151'
      }}>
        Training Results
      </div>
      <img
        src={data.imageSrc}
        alt={data.imageName}
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: '200px',
          objectFit: 'contain',
          borderRadius: '8px'
        }}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = `data:image/svg+xml,${encodeURIComponent(`
            <svg width='200' height='100' xmlns='http://www.w3.org/2000/svg'>
              <rect width='100%' height='100%' fill='#f3f4f6'/>
              <text x='50%' y='50%' font-family='Arial' font-size='12' fill='#6b7280' text-anchor='middle' dy='.3em'>
                Failed to load: ${data.imageName}
              </text>
            </svg>
          `)}`;
        }}
      />
    </div>
  );
};

// Node types for React Flow
const nodeTypes = {
  modelBlock: ModelBlockNode,
  imageNode: ImageNode,
};

// Edge types for React Flow
const edgeTypes = {
  variable: VariableEdge,
};



const PredictorModel: React.FC<PredictorModelProps> = ({ 
  predictorModelData,
  tabId,
  userID, 
  sessionId, 
  onTrainingMessage, 
  onResponse,
  images = []
}) => {
  const { makeAuthenticatedRequest } = useApiAuth();
  const [selectedModel, setSelectedModel] = useState<{name: string, model: ModelBlock} | null>(null);
  const [isTraining, setIsTraining] = useState(false);

  // Create a callback that takes name and model separately and creates the object
  const handleModelClick = useCallback((name: string, model: ModelBlock) => {
    setSelectedModel({ name, model });
  }, []);

  // Convert predictorModelData to ModelGraph format expected by the API
  const convertToModelGraph = useCallback(() => {
    if (!predictorModelData?.model_graph) return null;
    
    const { model_graph } = predictorModelData;
    
    // Convert nodes to API format
    const nodes = model_graph.nodes?.map((node: any) => {
      if (node.name === 'start' || node.name === 'end') {
        return { name: node.name };
      }
      return {
        name: node.name,
        model: 'model' in node ? node.model : undefined
      };
    });

    // Convert edges to API format
    const edges = model_graph.edges?.map((edge: any) => ({
      source_name: edge.source_name,
      target_name: edge.target_name,
      vars: edge.vars
    }));

    return { 
      nodes, 
      edges,
      model_name: model_graph.model_name 
    };
  }, [predictorModelData]);

  // Handle training with streaming
  const handleTrain = useCallback(async () => {
    if (!sessionId) {
      console.error('Missing userID or sessionId for training');
      return;
    }

    const modelGraph = convertToModelGraph();
    
    if (!modelGraph) {
      console.error('Could not convert model data to training format');
      return;
    }

    setIsTraining(true);
    
    // Send initial training message with a fixed ID so we can update it later
    const initialMessageId = `training-${Date.now()}`;
    if (onTrainingMessage) {
      onTrainingMessage({
        role: 'assistant',
        content: 'Starting model training...',
        id: initialMessageId
      });
    }
    
    try {
      const response = await makeAuthenticatedRequest(createApiUrl(`/ml/train_mlp/session_id/${sessionId}?tab_id=${tabId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modelGraph)
      });

      if (!response.ok) {
        throw new Error(`Training failed: ${response.statusText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let messageId = initialMessageId; // Use the same ID to update the initial message

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim()) {
              // Handle AI SDK streaming protocol format: 2:[{"type":"file","data":{...},"tab_destination":"modeling","reference_id":"..."}]
              if (line.startsWith('2:[')) {
                try {
                  const jsonStr = line.slice(2); // Remove '2:' prefix
                  const data = JSON.parse(jsonStr);
                  
                  if (Array.isArray(data) && data.length > 0) {
                    const item = data[0];
                    
                    // Pass the entire response to onResponse for proper handling
                    if (onResponse) {
                      onResponse([item]);
                    }
                  }
                } catch (e) {
                  console.error('Error parsing AI SDK streaming data:', e);
                }
              }
              // Handle text tokens (format like 0:"text here")
              else if (line.includes('"') && line.includes(':')) {
                try {
                  // Parse token to extract text from format like 0:"text here"
                  const colonIndex = line.indexOf(':');
                  if (colonIndex >= 0) {
                    const afterColon = line.slice(colonIndex + 1);
                    if (afterColon.startsWith('"') && afterColon.endsWith('"')) {
                      const text = afterColon.slice(1, -1); // Remove quotes
                      // Replace literal \n with actual new lines and Unicode escape sequences
                      let processedText = text.replace(/\\n/g, '\n');
                      // Handle Unicode escape sequences like \u00b2 (²)
                      processedText = processedText.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => {
                        return String.fromCharCode(parseInt(hex, 16));
                      });
                      accumulatedText += processedText;
                      
                      // Send text updates to chat
                      if (onTrainingMessage) {
                        onTrainingMessage({
                          role: 'assistant',
                          content: accumulatedText,
                          id: messageId
                        });
                      }
                    }
                  }
                } catch (e) {
                  console.error('Error parsing text token:', e);
                }
              }
              // Handle plain text lines (fallback)
              else {
                accumulatedText += line + '\n';
                if (onTrainingMessage) {
                  onTrainingMessage({
                    role: 'assistant',
                    content: accumulatedText,
                    id: messageId
                  });
                }
              }
            }
          }
        }
      }

      // Send final completion message to replace any remaining training text
      if (onTrainingMessage) {
        onTrainingMessage({
          role: 'assistant',
          content: accumulatedText || 'Model training completed successfully.',
          id: messageId
        });
      }
    } catch (error) {
      console.error('Training failed:', error);
      if (onTrainingMessage) {
        onTrainingMessage({
          role: 'assistant',
          content: `Training failed: ${error}`,
          id: `training-error-${Date.now()}`
        });
      }
    } finally {
      setIsTraining(false);
    }
  }, [userID, sessionId, convertToModelGraph, onTrainingMessage, onResponse]);

  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    if (!predictorModelData?.model_graph?.nodes || !predictorModelData?.model_graph?.edges) {
      return { nodes: [], edges: [] };
    }

    const { model_graph } = predictorModelData;

    // Calculate edge counts for each node
    const inputEdgeCounts = new Map<string, number>();
    const outputEdgeCounts = new Map<string, number>();
    
    model_graph.edges?.forEach((edge: any) => {
      // Count incoming edges for target nodes
      inputEdgeCounts.set(edge.target_name, (inputEdgeCounts.get(edge.target_name) || 0) + 1);
      // Count outgoing edges for source nodes
      outputEdgeCounts.set(edge.source_name, (outputEdgeCounts.get(edge.source_name) || 0) + 1);
    });

    // Position nodes with priority: 1. Cached position, 2. Backend position, 3. Grid layout
    const gridCols = Math.ceil(Math.sqrt(model_graph.nodes?.length || 0));
    const nodeSpacing = 300;

    // Create model nodes
    const modelNodes: Node[] = model_graph.nodes?.map((node: any, index: number) => {
      let position;
      
      // Priority: 1. Cached position, 2. Backend position, 3. Grid layout
      if (nodePositionCache.has(node.name)) {
        position = nodePositionCache.get(node.name)!;
      } else if (node.pos && node.pos.length === 2) {
        position = { x: node.pos[0], y: node.pos[1] };
      } else {
        // Fallback to grid layout
        const row = Math.floor(index / gridCols);
        const col = index % gridCols;
        position = { 
          x: col * nodeSpacing + 100, 
          y: row * nodeSpacing + 100 
        };
      }

      // Determine node type
      const isStartEnd = node.name === 'start' || node.name === 'end';
      return {
        id: node.name,
        type: 'modelBlock',
        position,
        data: {
          name: node.name,
          node_type: isStartEnd ? node.name : 'model',
          model: 'model' in node ? node.model : undefined,
          inputEdgeCount: inputEdgeCounts.get(node.name) || 0,
          outputEdgeCount: outputEdgeCounts.get(node.name) || 0,
          onModelClick: handleModelClick, // Pass the click handler
        },
      };
    }) || [];

    // Create image nodes for training results from tab images prop
    const imageNodes: Node[] = (images || []).map((image, index) => {
      const imageName = image.file_name;
      const imageId = `image-${index}`;
      let position;
      
      // Check if we have a cached position for this image
      if (nodePositionCache.has(imageId)) {
        position = nodePositionCache.get(imageId)!;
      } else {
        // Position images to the right of the model graph
        const maxModelX = Math.max(...modelNodes.map(node => node.position.x));
        const spacing = 350;
        position = {
          x: maxModelX + spacing,
          y: 100 + (index * 250) // Stack images vertically
        };
      }

      return {
        id: imageId,
        type: 'imageNode',
        position,
        draggable: true,
        data: {
          imageName,
          imageSrc: createApiUrl(`/file_handling/users/${userID}/sessions/${sessionId}/files/processed/file_types/plot/${encodeURIComponent(imageName)}`)
        }
      };
    });

    const allNodes = [...(modelNodes || []), ...imageNodes];

    const edges: Edge[] = (model_graph.edges || []).map((edge: any, index: number) => {
      return {
        id: `${edge.source_name}-${edge.target_name}-${index}`,
        source: edge.source_name,
        target: edge.target_name,
        type: 'variable',
        data: {
          vars: edge.vars
        },
      };
    });

    return { nodes: allNodes, edges };
  }, [predictorModelData, handleModelClick, images, userID, sessionId]);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Custom handler to save node positions to cache when they're moved
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    onNodesChange(changes);
    
    // Save positions to cache when nodes are moved
    changes.forEach(change => {
      if (change.type === 'position' && change.position && change.id) {
        nodePositionCache.set(change.id, change.position);
      }
    });
  }, [onNodesChange]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );  // Update nodes and edges when predictorModelData changes
  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);
  if (!predictorModelData || !predictorModelData.model_graph?.nodes?.length) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="text-lg font-medium">No Model Data</div>
          <div className="text-sm">Ask the AI to create a predictor model</div>
        </div>
      </div>
    );
  }  return (
    <div className="w-full h-full flex flex-col">
      {/* Modern Header with Train & Fit button */}
      <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-white to-gray-50/50 border-b border-gray-200/60 backdrop-blur-sm select-none">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-6 rounded-full" style={{ backgroundColor: 'rgb(30, 156, 215)' }}></div>
          <h2 
            className="text-xl font-light tracking-wide" 
            style={{ 
              fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', 
              color: 'rgb(30, 156, 215)',
              letterSpacing: '0.5px'
            }}
          >
            Predictor Model
          </h2>
        </div>
        <button
          onClick={handleTrain}
          disabled={isTraining || !userID || !sessionId}
          className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-300 select-none focus:outline-none focus:ring-2 focus:ring-blue-200 ${
            isTraining || !userID || !sessionId
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-inner'
              : 'text-white hover:shadow-lg hover:scale-105 cursor-pointer transform active:scale-95'
          }`}
          style={{
            background: isTraining || !userID || !sessionId 
              ? undefined 
              : 'linear-gradient(135deg, rgba(30, 156, 215, 0.8) 0%, rgba(30, 156, 215, 0.9) 100%)',
            userSelect: 'none',
            boxShadow: isTraining || !userID || !sessionId 
              ? undefined 
              : '0 4px 15px rgba(30, 156, 215, 0.2)'
          }}
        >
          {isTraining ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Training...</span>
            </div>
          ) : (
            'Train'
          )}
        </button>
      </div>

      {/* Equation Modal */}
      {selectedModel && selectedModel.model && (
        <EquationModal 
          selectedModel={selectedModel}
          onClose={() => setSelectedModel(null)}
        />
      )}

      <div className="flex-1 overflow-hidden">
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }} // Add padding for better view when using backend positions
            attributionPosition="bottom-left"
            nodesDraggable={true} // Explicitly enable node dragging (default is true)
            style={{ width: '100%', height: '100%' }}
          >
            <Controls />
            
            {/* Arrow marker for edges */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon
                  points="0 0, 10 3.5, 0 7"
                  fill="#1e9cd7"
                />
              </marker>
            </defs>
          </ReactFlow>
        </div>
    </div>
  );
};

export default PredictorModel;
